/**
 * Supabase-Compatible Facade
 *
 * Drop-in replacement for `import { supabase } from '@/integrations/supabase/client'`.
 * Routes every call through PMS abstractions so the raw client is never
 * imported outside `src/integrations/pms/`.
 *
 * Usage:
 *   import { supabase } from '@/integrations/pms/compat';
 */

import { pmsFrom } from './query';
import { pmsAuth } from './auth';
import { invokeFunction } from './functions';
import { pmsStorage } from './storage';
import { pmsChannel, pmsRemoveChannel } from './realtime';
import { pmsRpc } from './rpc';

export const supabase = {
  from: pmsFrom,

  auth: {
    getUser: async () => {
      const user = await pmsAuth.getUserSafe();
      return { data: { user }, error: null as any };
    },
    getSession: async () => {
      try {
        const session = await pmsAuth.getSession();
        return { data: { session }, error: null as any };
      } catch (e: any) {
        return { data: { session: null }, error: e };
      }
    },
    signInWithPassword: async (creds: any) => {
      try {
        const data = await pmsAuth.signInWithPassword(creds);
        return { data, error: null as any };
      } catch (e: any) {
        return { data: { user: null, session: null }, error: e };
      }
    },
    signUp: async (opts: any) => {
      try {
        const data = await pmsAuth.signUp({
          email: opts.email,
          password: opts.password,
          fullName: opts.options?.data?.full_name,
          metadata: opts.options?.data,
        }, { emailRedirectTo: opts.options?.emailRedirectTo });
        return { data, error: null as any };
      } catch (e: any) {
        return { data: { user: null, session: null }, error: e };
      }
    },
    signOut: async () => {
      try {
        await pmsAuth.signOut();
        return { error: null as any };
      } catch (e: any) {
        return { error: e };
      }
    },
    onAuthStateChange: (cb: any) => {
      const unsub = pmsAuth.onAuthStateChange(cb);
      return { data: { subscription: { unsubscribe: unsub } } };
    },
    resetPasswordForEmail: async (email: string, opts?: any) => {
      try {
        await pmsAuth.resetPasswordForEmail(email, opts?.redirectTo);
        return { error: null as any };
      } catch (e: any) {
        return { error: e };
      }
    },
    updateUser: async (attrs: any) => {
      try {
        const data = await pmsAuth.updateUser(attrs);
        return { data, error: null as any };
      } catch (e: any) {
        return { data: null, error: e };
      }
    },
    setSession: async (tokens: any) => {
      try {
        const data = await pmsAuth.setSession(tokens);
        return { data, error: null as any };
      } catch (e: any) {
        return { data: null, error: e };
      }
    },
    refreshSession: async (params: any) => {
      try {
        const data = await pmsAuth.refreshSession(params);
        return { data, error: null as any };
      } catch (e: any) {
        return { data: null, error: e };
      }
    },
  },

  functions: {
    invoke: async (name: string, opts?: any) => {
      try {
        const result = await invokeFunction(name, opts);
        return { data: result.data, error: null as any };
      } catch (e: any) {
        return { data: null, error: e };
      }
    },
  },

  storage: {
    from: (bucket: string) => ({
      upload: async (path: string, file: any, opts?: any) => {
        try {
          const result = await pmsStorage.upload(bucket, path, file, opts);
          return { data: { path: result.path }, error: null as any };
        } catch (e: any) {
          return { data: null, error: e };
        }
      },
      getPublicUrl: (path: string) => {
        const publicUrl = pmsStorage.getPublicUrl(bucket, path);
        return { data: { publicUrl } };
      },
      remove: async (paths: string[]) => {
        try {
          await pmsStorage.remove(bucket, paths);
          return { data: null, error: null as any };
        } catch (e: any) {
          return { data: null, error: e };
        }
      },
      list: async (folderPath?: string) => {
        try {
          const data = await pmsStorage.list(bucket, folderPath);
          return { data, error: null as any };
        } catch (e: any) {
          return { data: null, error: e };
        }
      },
    }),
  },

  channel: pmsChannel,
  removeChannel: pmsRemoveChannel,
  rpc: async (fn: string, params?: any) => {
    try {
      const data = await pmsRpc(fn, params);
      return { data, error: null as any };
    } catch (e: any) {
      return { data: null, error: e };
    }
  },
};
